import React from "react";
import {
  Box,
  Flex,
  Text,
  Icon,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalHeader,
  ModalCloseButton,
  IconButton,
  useDisclosure,
  Divider,
} from "@chakra-ui/react";
import { BsTable, BsKanban } from "react-icons/bs";
import { TbCalendarPlus } from "react-icons/tb";
import { BiMessageRoundedAdd } from "react-icons/bi";
import { FaRegSquareCheck } from "react-icons/fa6";
import { RiLogoutCircleRLine } from "react-icons/ri";
import { IoTrashOutline } from "react-icons/io5";
import InputSearch from "@components/Sales/Inputs/InputSearch";
import salesService from "@services/Sales/SalesService";
import Alert from "@components/common/Alert";
import { LoginContext } from "@context/loginContext";
import TableMyOportunity from "@components/Sales/TableMyOportunity";
import InputSelect from "@components/Sales/Inputs/InputSelect";
import { optionFase, optionOpportunityCreator } from "@helpers/optionSelect";
import Spinner from "@components/common/Spinner";
import { formatDatePtBr } from "@helpers/format";
import InputText from "@components/Sales/Inputs/InputText";
import Button from "@components/common/Button";
import { RxUpdate } from "react-icons/rx";
import type { TableRow } from "src/@types/Sales";
import { theme } from "src/styles/theme";
import { OportunityContext } from "@context/oportunityContex";
import { UsuallyContext } from "@context/usuallyContex";
import useWindowDimensions from "@hook/useWindowDimensions";
import { DEFAULT_PHASES } from "@helpers/defaultValues";
import { FiMaximize2 } from "react-icons/fi";
import { cookieKeys, getCookie } from "@helpers/cookies";
import { MdArrowBack } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import oportunityService from "@services/Oportunity/OportunityService";
import oportunityServiceInstance from "@services/Oportunity/OportunityService";
import { useMessageErrorToast } from "@hook/useMessageErrorToast";
import { useOnlyMessageToast } from "@hook/useMessageToast";
import KanbanMyOportunity from "../../components/KanbanMyOportunity";
import InputSortBy, {
  OrdenarPor,
  TipoOrdenacao,
} from "@components/Sales/Inputs/InputSortBy";

const MyOportunityComponentOriginalWrapper = ({ navigation }: any) => {
  const idCorretor = getCookie(cookieKeys.idCorretor) || "";
  const navigate = useNavigate();
  const displayMessageError = useMessageErrorToast();
  const displayMessage = useOnlyMessageToast();
  const { signOut } = React.useContext(LoginContext);
  const {
    faseMyOportunity: faseFromContext,
    faseHomeFilter,
    setFaseHomeFilter,
  } = React.useContext(OportunityContext);
  const { setHashSalesSidebar, setHashOportunity, setHashDetailOportunity } =
    React.useContext(UsuallyContext);
  const {
    setLoading,
    setContracts,
    setAccessCreateOp,
    setFaseOp,
    setImobiliariaId,
    setImobiliariaNome,
    setIdEmpreendimentoSales,
    setNomeEmpreendimentoSales,
    setIndexTabActivityComponent,
    setAccessQuote,
    setIndexTabDetails,
  } = React.useContext(OportunityContext);

  // ── View mode ─────────────────────────────────────────────────────────────
  const [viewMode, setViewMode] = React.useState<"tabela" | "quadro">("tabela");

  // ── Filters ───────────────────────────────────────────────────────────────
  const [faseMyOportunityLocal, setFaseMyOportunityLocal] = React.useState<
    string[] | string
  >(() => (Array.isArray(faseFromContext) ? faseFromContext : []));
  // Fases selecionadas no filtro exclusivamente para o modo Kanban — começa
  // vazio, ou seja, nenhuma coluna carrega dados até o usuário selecionar
  // pelo menos uma fase no filtro.
  const [faseKanbanLocal, setFaseKanbanLocal] = React.useState<string[]>([]);
  // Incrementado para forçar uma nova busca em todas as colunas do Kanban
  // quando nenhum filtro mudou (clique no botão "Atualizar"). Não é usado
  // pelo "Limpar filtros" — veja o comentário em handleClearFilters.
  const [kanbanRefreshKey, setKanbanRefreshKey] = React.useState(0);

  // Mesma lógica de "filtro" (categoria especial) e "rank" (ranking) usada
  // no modo tabela, só que calculada a partir da seleção de fase do Kanban.
  // Isso garante que selecionar "Bronze", "Oportunidades vencidas", etc no
  // filtro também funcione dentro do Kanban.
  const kanbanFiltroRank = React.useMemo(() => {
    const currentFases = faseKanbanLocal;

    const isFilterFase =
      currentFases.some((f) =>
        f.toLowerCase().includes("oportunidades vencidas"),
      ) ||
      currentFases.includes("OP's Cliente Ouro") ||
      currentFases.includes("Contrato");

    const handleNumberFase = () => {
      if (
        currentFases.some((f) =>
          f.toLowerCase().includes("oportunidades vencidas"),
        )
      )
        return 1;
      if (
        currentFases.some((f) =>
          f.toLowerCase().includes("aguardando atendimento"),
        )
      )
        return 2;
      if (currentFases.some((f) => f.toLowerCase().includes("contrato")))
        return 4;
      if (currentFases.some((f) => f.toLowerCase().includes("cliente ouro")))
        return 5;
      return 3;
    };

    const handleNumberRank = () => {
      if (currentFases.some((f) => f.toLowerCase().includes("bronze")))
        return 1;
      if (currentFases.some((f) => f.toLowerCase().includes("prata"))) return 2;
      if (currentFases.some((f) => f.toLowerCase().includes("ouro"))) return 3;
      if (currentFases.some((f) => f.toLowerCase().includes("diamante")))
        return 4;
      if (currentFases.some((f) => f.toLowerCase().includes("aço"))) return 5;
      if (currentFases.some((f) => f.toLowerCase().includes("não elegível")))
        return 6;
      return null;
    };

    return {
      filtro: isFilterFase ? handleNumberFase() : undefined,
      rank: handleNumberRank(),
    };
  }, [faseKanbanLocal]);

  // Sincroniza a seleção do Kanban com o filtro vindo da Home apenas na
  // PRIMEIRA vez que o usuário entra no modo quadro — e isso é feito no
  // próprio clique da aba (ver onClick da aba "Ver como quadro" abaixo),
  // não em um useEffect. Se fizéssemos essa sincronização num efeito (que
  // só roda depois do mount), o Kanban chegaria a montar uma primeira vez
  // sem filtro nenhum (mostrando os dados "default" de cada coluna) e só
  // segundos depois — após o efeito rodar e o debounce interno do Kanban
  // resolver — receberia a fase correta, causando o "pisca" reportado.
  // Fazendo a sincronização no clique, os dois estados (fase + viewMode)
  // são aplicados juntos e o Kanban já nasce com a fase certa.
  //
  // Depois da primeira vez, a seleção do Kanban passa a ser controlada só
  // pelo usuário (seja escolhendo uma fase no filtro, seja em "Limpar
  // filtros"), sem ser sobrescrita a cada troca de aba Tabela <-> Quadro.
  const hasSyncedKanbanFromHomeRef = React.useRef(false);
  // Congela o valor de faseHomeFilter assim que o componente monta, ANTES que
  // qualquer interação no filtro da tabela possa sobrescrevê-lo via
  // setFaseHomeFilter([]). Isso garante que a sincronização inicial do Kanban
  // não seja afetada por mudanças feitas depois no filtro da tabela.
  const initialFaseHomeFilterRef = React.useRef(faseHomeFilter);

  const [isUnauthorized, setIsUnauthorized] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [dataOportunity, setDataOportunity] = React.useState<any[]>([]);
  const [loading, setLoadingLocal] = React.useState(true);
  const [newSearch, setNewSearch] = React.useState("");
  const [oportunityCreator, setOportunityCreator] = React.useState<
    string | string[]
  >([]);
  const [dateStart, setDateStart] = React.useState("");
  const [dateEnd, setDateEnd] = React.useState("");
  const [filtersLoaded, setFiltersLoaded] = React.useState(false);
  const [loadingCreateCall, setLoadingCreateCall] = React.useState(false);
  const [ordenarPor, setOrdenarPor] = React.useState<OrdenarPor>("ranking");
  const [tipoOrdenacao, setTipoOrdenacao] =
    React.useState<TipoOrdenacao>("asc");

  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(0);
  const [totalRegistros, setTotalRegistros] = React.useState(0);

  const ITEMS_PER_PAGE = 50;

  // ── Fases memo ────────────────────────────────────────────────────────────
  const fases = React.useMemo(() => {
    const fromLocal = Array.isArray(faseMyOportunityLocal)
      ? faseMyOportunityLocal
      : [faseMyOportunityLocal];
    const fromContext = Array.isArray(faseFromContext)
      ? faseFromContext
      : [faseFromContext];
    const isFromContext =
      fromLocal.length === fromContext.length &&
      fromLocal.every((f) => fromContext.includes(f));
    return isFromContext ? fromContext : fromLocal;
  }, [faseMyOportunityLocal, faseFromContext]);

  const faseToDisplay = React.useMemo(() => {
    const local = Array.isArray(faseMyOportunityLocal)
      ? faseMyOportunityLocal
      : [faseMyOportunityLocal];
    const context = Array.isArray(faseFromContext)
      ? faseFromContext
      : [faseFromContext];
    const isFromContext =
      local.length === context.length &&
      local.every((f) => context.includes(f));
    return isFromContext ? context : local;
  }, [faseMyOportunityLocal, faseFromContext]);

  // ── API ───────────────────────────────────────────────────────────────────
  const handleGetOportunity = React.useCallback(
    async (
      page = 1,
      overrides?: {
        search?: string;
        fases?: string[];
        oportunityCreator?: string | string[];
        dateStart?: string;
        dateEnd?: string;
        ordenarPor?: OrdenarPor;
        tipoOrdenacao?: TipoOrdenacao;
      },
    ) => {
      setLoadingLocal(true);

      const currentSearch = overrides?.search ?? search;
      const currentFases = overrides?.fases ?? fases;
      const currentOpportunityCreator =
        overrides?.oportunityCreator ?? oportunityCreator;
      const currentDateStart = overrides?.dateStart ?? dateStart;
      const currentDateEnd = overrides?.dateEnd ?? dateEnd;
      const currentOrdenarPor = overrides?.ordenarPor ?? ordenarPor;
      const currentTipoOrdenacao = overrides?.tipoOrdenacao ?? tipoOrdenacao;

      const handleNumberFase = () => {
        if (
          currentFases.some((f) =>
            f.toLowerCase().includes("oportunidades vencidas"),
          )
        )
          return 1;
        if (
          currentFases.some((f) =>
            f.toLowerCase().includes("aguardando atendimento"),
          )
        )
          return 2;
        if (currentFases.some((f) => f.toLowerCase().includes("contrato")))
          return 4;
        if (currentFases.some((f) => f.toLowerCase().includes("cliente ouro")))
          return 5;
        return 3;
      };

      const handleNumberRank = () => {
        if (currentFases.some((f) => f.toLowerCase().includes("bronze")))
          return 1;
        if (currentFases.some((f) => f.toLowerCase().includes("prata")))
          return 2;
        if (currentFases.some((f) => f.toLowerCase().includes("ouro")))
          return 3;
        if (currentFases.some((f) => f.toLowerCase().includes("diamante")))
          return 4;
        if (currentFases.some((f) => f.toLowerCase().includes("aço"))) return 5;
        if (currentFases.some((f) => f.toLowerCase().includes("não elegível")))
          return 6;
        return null;
      };

      const isFilterFase =
        currentFases.some((f) =>
          f.toLowerCase().includes("oportunidades vencidas"),
        ) ||
        currentFases.includes("OP's Cliente Ouro") ||
        currentFases.includes("Contrato");

      const filter = isFilterFase ? handleNumberFase() : undefined;

      try {
        const response = await salesService.getOportunity({
          idCorretorSalesforce: idCorretor,
          DataInicio: currentDateStart,
          DataFim: currentDateEnd,
          filtro: filter,
          rank: handleNumberRank(),
          filtroCriadorOportunidade: currentOpportunityCreator,
          fase: currentFases,
          clienteOuIdDaOportunidade: currentSearch,
          itensPorPagina: ITEMS_PER_PAGE,
          Pagina: page,
          ordenarPor: currentOrdenarPor,
          tipoOrdenacao: currentTipoOrdenacao,
        });

        if (response.status === 401) {
          setIsUnauthorized(true);
          setDataOportunity([]);
          setTotalPages(0);
          setTotalRegistros(0);
          setCurrentPage(1);
          setLoadingLocal(false);
          return;
        }
        if (response.status === 400) {
          setDataOportunity([]);
          setTotalPages(0);
          setTotalRegistros(0);
          setCurrentPage(1);
          setLoadingLocal(false);
          return;
        }

        setDataOportunity(Array.isArray(response?.itens) ? response.itens : []);
        setCurrentPage(response?.paginaAtual ?? page);
        setTotalPages(response?.totalPaginas ?? 0);
        setTotalRegistros(response?.totalRegistros ?? 0);
        setNewSearch(search);
      } catch (error) {
        console.log("errorHandleGetSales", error);
      } finally {
        setLoadingLocal(false);
      }
    },
    [
      fases,
      idCorretor,
      dateStart,
      dateEnd,
      oportunityCreator,
      search,
      ordenarPor,
      tipoOrdenacao,
    ],
  );

  const handleClickKanbanCard = React.useCallback(
    (row: any) => {
      localStorage.removeItem("@notificationNavigation");
      localStorage.removeItem("@indexTabDetails");
      localStorage.removeItem("@indexTabActivityComponent");
      localStorage.removeItem("@notificationType");

      localStorage.setItem(
        "@oportunidade_nova_aba",
        JSON.stringify({
          oportunidadeId: row?.oportunidadeId,
          nomeDaConta: row?.nomeDaConta,
          opportunityIdSalesforce: row?.opportunityIdSalesforce,
          fase: row?.fase,
          empreendimentoDeInteresse: row?.empreendimentoDeInteresse,
          idEmpreendimentoSales: row?.idEmpreendimentoSales,
          imobiliaria: row?.imobiliaria,
          imobiliariaId: row?.imobiliariaId,
        }),
      );

      setAccessCreateOp({
        oportunidadeId: row.oportunidadeId,
        nomeDaConta: row.nomeDaConta,
        oportunityIdSalesforce: row.opportunityIdSalesforce,
      });
      setLoading(true);

      oportunityService
        .getContracts({
          opportunityIdSalesforce: row.opportunityIdSalesforce,
        })
        .then((c) => {
          setContracts(c);
          setLoading(false);
        });

      navigate(`/oportunidades/minhas-oportunidades/detalhes`, {
        state: { oportunidadeId: row.oportunidadeId },
      });
      navigation();

      setHashDetailOportunity("DetailOportunity");
      setHashOportunity("DetailOportunityComponent");
      setHashSalesSidebar("DetailOportunityComponent");
      setIndexTabDetails(0);
      setFaseOp(
        row?.fase?.toLowerCase() === "fechado e ganho" ||
          row?.fase?.toLowerCase() === "fechado e perdido",
      );
      setNomeEmpreendimentoSales(row?.empreendimentoDeInteresse);
      setIdEmpreendimentoSales(row?.idEmpreendimentoSales);
      setImobiliariaNome(row?.imobiliaria);
      setImobiliariaId(row?.imobiliariaId);
    },
    [
      navigation,
      navigate,
      setAccessCreateOp,
      setContracts,
      setFaseOp,
      setHashDetailOportunity,
      setHashOportunity,
      setHashSalesSidebar,
      setIdEmpreendimentoSales,
      setImobiliariaId,
      setImobiliariaNome,
      setIndexTabDetails,
      setLoading,
      setNomeEmpreendimentoSales,
    ],
  );

  // ── Create call ───────────────────────────────────────────────────────────
  const handleCreateTasks = React.useCallback(
    async (dropdownOp: any) => {
      setLoadingCreateCall(true);
      const getDateToDay = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        return `${day}/${month}/${year}`;
      };
      try {
        const response = await oportunityServiceInstance.createTask({
          idCorretorSalesforce: idCorretor,
          assunto: "Chamada",
          dataDeVencimento: getDateToDay(),
          relativoA: dropdownOp.opportunityIdSalesforce,
          status: "Concluído",
          tipoDeRegistro: "Call",
          numeroDeTentativas: 1,
          comentarios:
            "Chamada criada automaticamente pelo corretor, através do botão de whatsapp do cliente no Bora Vender.",
        });
        if (response.status >= 400 && response?.status !== 401) {
          setLoadingCreateCall(false);
          displayMessageError({
            message: response?.data?.mensagem ?? "Erro ao criar chamada!",
          });
          return;
        }
        if (response.status === 401) {
          setLoadingCreateCall(false);
          return;
        }
        displayMessage("Chamada registrada com sucesso!");
        setLoadingCreateCall(false);
      } catch (error) {
        setLoadingCreateCall(false);
        console.log("errorHandleCreateTask", error);
      }
    },
    [displayMessage, displayMessageError, idCorretor],
  );

  // ── Dropdown click ────────────────────────────────────────────────────────
  const handleClickButtonDropdown = React.useCallback(
    async (dropdownOp: any, titleDropdown: string) => {
      try {
        setIndexTabDetails(0);
        setIndexTabActivityComponent(0);
        localStorage.setItem("@indexTabDetails", "0");
        localStorage.setItem("@indexTabActivityComponent", "0");
        localStorage.setItem("@notificationNavigation", "true");
        localStorage.setItem("@notificationType", dropdownOp?.tipoNotificacao);
        setHashOportunity("DetailOportunityComponent");
        setAccessCreateOp((prevState: any) => ({
          ...prevState,
          oportunidadeId: dropdownOp?.oportunidadeId,
          oportunityIdSalesforce:
            dropdownOp?.opportunityIdSalesforce ||
            prevState.oportunityIdSalesforce,
        }));

        if (titleDropdown === "Criar agendamento") {
          setHashDetailOportunity("DetailOportunity");
          navigate(`/oportunidades/minhas-oportunidades/detalhes`, {
            replace: true,
            state: {
              source: "dropdownOp",
              type: "CriarAgendamento",
              indexTabDetails: 2,
              indexTabActivityComponent: 1,
              oportunidadeId: dropdownOp?.oportunidadeId,
            },
          });
        } else if (titleDropdown === "Abrir Whatsapp") {
          let phone = dropdownOp?.telefonePrincipal?.replace(/\D/g, "");
          if (!phone) return;
          if (!phone.startsWith("55")) phone = "55" + phone;
          const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(
            navigator.userAgent,
          );
          void handleCreateTasks(dropdownOp);
          if (isMobileDevice) {
            window.open(`https://wa.me/${phone}`, "_blank");
          } else {
            window.open(
              `https://web.whatsapp.com/send?phone=${phone}`,
              "_blank",
            );
          }
        } else if (titleDropdown === "Criar tarefa") {
          setHashDetailOportunity("DetailOportunity");
          navigate(`/oportunidades/minhas-oportunidades/detalhes`, {
            replace: true,
            state: {
              source: "dropdownOp",
              type: "CriarTarefa",
              indexTabDetails: 2,
              indexTabActivityComponent: 0,
              oportunidadeId: dropdownOp?.oportunidadeId,
            },
          });
        } else if (titleDropdown === "Fechar e perder oportunidade") {
          setHashDetailOportunity("DetailOportunity");
          navigate(`/oportunidades/minhas-oportunidades/detalhes`, {
            replace: true,
            state: {
              source: "dropdownOp",
              type: "FecharPerder",
              indexTabDetails: 0,
              indexTabActivityComponent: 0,
              oportunidadeId: dropdownOp?.oportunidadeId,
            },
          });
        } else {
          setHashDetailOportunity("DetailOportunity");
          setAccessQuote({
            nomeDaCotacao: dropdownOp?.nomeDaCotacao,
            idCotacaoSalesforce: dropdownOp?.idCotacaoSalesforce,
          });
          navigate(`/oportunidades/minhas-oportunidades/detalhes`, {
            replace: true,
            state: {
              source: "dropdownOp",
              type: "CriarCotacao",
              indexTabDetails: 4,
              indexTabActivityComponent: 0,
              oportunidadeId: dropdownOp?.oportunidadeId,
            },
          });
        }
      } catch (error) {
        console.error(
          "Erro ao processar o redirecionamento do dropdown:",
          error,
        );
      }
    },
    [
      handleCreateTasks,
      navigate,
      setAccessCreateOp,
      setAccessQuote,
      setHashDetailOportunity,
      setHashOportunity,
      setIndexTabActivityComponent,
      setIndexTabDetails,
    ],
  );

  const arrayButtons = React.useMemo(
    () => [
      {
        icon: <TbCalendarPlus fontSize="18px" />,
        title: "Criar agendamento",
      },
      {
        icon: <BiMessageRoundedAdd fontSize="18px" />,
        title: "Abrir Whatsapp",
      },
      { icon: <FaRegSquareCheck fontSize="18px" />, title: "Criar tarefa" },
      {
        icon: <RiLogoutCircleRLine fontSize="18px" />,
        title: "Criar cotação",
      },
      {
        icon: <IoTrashOutline fontSize="18px" />,
        title: "Fechar e perder oportunidade",
      },
    ],
    [],
  );

  const renderDropdown = React.useCallback(
    (row: any) =>
      arrayButtons
        .filter(
          (button) =>
            !(
              button.title === "Fechar e perder oportunidade" &&
              row.PermitidoQueda === false
            ),
        )
        .map((button) => (
          <Box w="100%" key={button.title}>
            <Flex flexDir={"column"} w="100%" justifyContent={"center"}>
              <Box
                display={"flex"}
                p={"10px"}
                gap={"15px"}
                alignItems={"center"}
                opacity={"0.8"}
                _hover={{ opacity: "1" }}
                cursor="pointer"
                onClick={() => handleClickButtonDropdown(row, button.title)}
              >
                <Box w={"fit-content"} color={theme.text}>
                  {button.icon}
                </Box>
                <Text
                  w={"fit-content"}
                  fontSize={"13px"}
                  fontWeight={"medium"}
                  color={theme.text}
                  _hover={{ color: theme.primary }}
                >
                  {button.title}
                </Text>
              </Box>
              <Divider
                orientation="horizontal"
                borderColor={"#E5E9F0"}
                h={"2px"}
              />
            </Flex>
          </Box>
        )),
    [arrayButtons, handleClickButtonDropdown],
  );

  // ── Filter helpers ────────────────────────────────────────────────────────
  const handleAdjustNameFase = (faseMyOportunity: string | string[]) => {
    if (Array.isArray(faseMyOportunity)) {
      return faseMyOportunity
        .map((fase) =>
          fase.toLowerCase().includes("oportunidades vencidas")
            ? fase.split("-")[0]?.trim()
            : fase,
        )
        .join(", ");
    } else {
      if (faseMyOportunity.toLowerCase().includes("oportunidades vencidas"))
        return faseMyOportunity.split("-")[0]?.trim();
      return faseMyOportunity;
    }
  };

  const handleAdjustName = (value: string | string[]) => {
    if (Array.isArray(value)) return value.join(", ");
    return value;
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    localStorage.setItem("@myOpp_search", value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length >= 3 || value.length === 0) {
      debounceRef.current = setTimeout(() => {
        handleGetOportunity(1, { search: value });
      }, 1000);
    }
  };

  const handleFaseChange = (
    _e: React.ChangeEvent<HTMLInputElement>,
    selected: string[] | string,
  ) => {
    const allOptionNames = optionFase.map((option) => option.name);
    // Opções "reais", excluindo o item sintético "Todas"
    const realOptionNames = allOptionNames.filter((name) => name !== "Todas");

    // Verifica se um array de fases (sem "Todas") cobre todas as opções reais
    const coversAll = (arr: string[]) => {
      const withoutTodas = arr.filter((f) => f !== "Todas");
      return (
        withoutTodas.length === realOptionNames.length &&
        realOptionNames.every((name) => withoutTodas.includes(name))
      );
    };

    if (viewMode === "quadro") {
      let updatedKanbanFases: string[] = [];
      const prevHasTodasKanban = faseKanbanLocal.includes("Todas");
      if (typeof selected === "string") {
        updatedKanbanFases = [selected];
      } else if (selected.includes("Todas") && !prevHasTodasKanban) {
        updatedKanbanFases = allOptionNames;
      } else if (!selected.includes("Todas") && prevHasTodasKanban) {
        updatedKanbanFases = [];
      } else {
        updatedKanbanFases = selected.filter((f) => f !== "Todas");
        if (coversAll(updatedKanbanFases)) {
          updatedKanbanFases = allOptionNames;
        }
      }
      setFaseKanbanLocal(updatedKanbanFases);
      setKanbanRefreshKey((k) => k + 1);
      return;
    }

    // ── Modo Tabela ──
    let updatedFases: string[] = [];
    if (typeof selected === "string") {
      updatedFases = [selected];
    } else {
      const hasTodas = selected.includes("Todas");
      const prevHasTodas = faseMyOportunityLocal.includes("Todas");
      if (hasTodas && !prevHasTodas) {
        updatedFases = allOptionNames;
      } else if (!hasTodas && prevHasTodas) {
        updatedFases = [];
      } else {
        updatedFases = selected.filter((f) => f !== "Todas");
        if (coversAll(updatedFases)) {
          updatedFases = allOptionNames;
        }
      }
    }
    setCurrentPage(1);
    setFaseHomeFilter([]);
    setFaseMyOportunityLocal(updatedFases);
    localStorage.setItem("@myOpp_fase", JSON.stringify(updatedFases));
    handleGetOportunity(1, {
      fases: updatedFases,
      search,
      oportunityCreator,
      dateStart,
      dateEnd,
    });
  };

  const handleOportunityCreatorChange = (
    _e: React.ChangeEvent<HTMLInputElement>,
    selected: string | string[],
  ) => {
    setCurrentPage(1);
    setOportunityCreator(selected);
    localStorage.setItem("@myOpp_oportunityCreator", JSON.stringify(selected));
    handleGetOportunity(1, {
      oportunityCreator: selected,
      search,
      fases,
      dateStart,
      dateEnd,
    });
  };

  const handleSortChange = (
    novoOrdenarPor: OrdenarPor,
    novoTipoOrdenacao: TipoOrdenacao,
  ) => {
    setOrdenarPor(novoOrdenarPor);
    setTipoOrdenacao(novoTipoOrdenacao);
    localStorage.setItem("@myOpp_ordenarPor", novoOrdenarPor);
    localStorage.setItem("@myOpp_tipoOrdenacao", novoTipoOrdenacao);
    handleGetOportunity(1, {
      search,
      fases,
      oportunityCreator,
      dateStart,
      dateEnd,
      ordenarPor: novoOrdenarPor,
      tipoOrdenacao: novoTipoOrdenacao,
    } as any);
  };

  const handleDateStartChange = (value: string) => {
    const formatted = formatDatePtBr(value);
    setDateStart(formatted);
    localStorage.setItem("@myOpp_dateStart", formatted);
    if (formatted.length === 10 || formatted.length === 0) {
      handleGetOportunity(1, {
        dateStart: formatted,
        dateEnd,
        search,
        fases,
        oportunityCreator,
      });
    }
  };

  const handleDateEndChange = (value: string) => {
    const formatted = formatDatePtBr(value);
    setDateEnd(formatted);
    localStorage.setItem("@myOpp_dateEnd", formatted);
    if (formatted.length === 10 || formatted.length === 0) {
      handleGetOportunity(1, {
        dateEnd: formatted,
        dateStart,
        search,
        fases,
        oportunityCreator,
      });
    }
  };

  const handleClearFilters = () => {
    const clearedSearch = "";
    const clearedFases = DEFAULT_PHASES;
    const clearedOpportunityCreator: string[] = [];
    const clearedDateStart = "";
    const clearedDateEnd = "";

    localStorage.removeItem("@myOpp_search");
    localStorage.setItem("@myOpp_fase", JSON.stringify(clearedFases));
    localStorage.removeItem("@myOpp_oportunityCreator");
    localStorage.removeItem("@myOpp_dateStart");
    localStorage.removeItem("@myOpp_dateEnd");

    setSearch(clearedSearch);
    setFaseMyOportunityLocal(clearedFases);
    setOportunityCreator(clearedOpportunityCreator);
    setDateStart(clearedDateStart);
    setDateEnd(clearedDateEnd);
    setCurrentPage(1);

    // Só zera a fase do Kanban se o usuário estiver de fato na aba Kanban.
    // Assim, limpar os filtros na Tabela não derruba uma seleção que o
    // usuário já tinha feito no Kanban (e vice-versa continua intocado,
    // já que handleFaseChange nunca mexe em faseMyOportunityLocal a
    // partir do modo quadro).
    if (viewMode === "quadro") {
      setFaseKanbanLocal([]);
    }

    handleGetOportunity(1, {
      search: clearedSearch,
      fases: clearedFases,
      oportunityCreator: clearedOpportunityCreator,
      dateStart: clearedDateStart,
      dateEnd: clearedDateEnd,
    });
  };

  // ── Filtered rows ─────────────────────────────────────────────────────────
  const filteredRows = React.useMemo(() => {
    if (!dataOportunity || dataOportunity.length === 0) return [];

    const faseArray = Array.isArray(faseMyOportunityLocal)
      ? faseMyOportunityLocal
      : faseMyOportunityLocal
        ? [faseMyOportunityLocal]
        : [];

    let filteredData = dataOportunity.filter((row: TableRow) => {
      const isRankingFilter = faseArray.some((item) => {
        const itemLower = item?.toLowerCase() || "";
        return (
          itemLower.includes("diamante") ||
          itemLower.includes("ouro") ||
          itemLower.includes("prata") ||
          itemLower.includes("bronze") ||
          itemLower.includes("aço") ||
          itemLower.includes("não elegível") ||
          itemLower.includes("ranking não disponível")
        );
      });
      const isSpecialFilter = faseArray.some((item) => {
        const itemLower = item?.toLowerCase() || "";
        return (
          itemLower.includes("oportunidades vencidas") ||
          itemLower.includes("cliente ouro")
        );
      });

      let isMatchingPhase = true;
      if (!(isRankingFilter || isSpecialFilter)) {
        if (
          !faseMyOportunityLocal ||
          (Array.isArray(faseMyOportunityLocal) &&
            faseMyOportunityLocal.length === 0)
        ) {
          isMatchingPhase = true;
        } else {
          const rowFase = row.fase?.toLowerCase() || "";
          isMatchingPhase = faseArray.some((faseItem) => {
            const lowerFase = faseItem?.toLowerCase() || "";
            if (lowerFase === "" || lowerFase === "todas") return true;
            return rowFase === lowerFase;
          });
        }
      }
      if (!isMatchingPhase) return false;
      return true;
    });

    const uniqueMap = new Map<string, TableRow>();
    filteredData.forEach((row) => {
      if (!uniqueMap.has(row.oportunidadeId))
        uniqueMap.set(row.oportunidadeId, row);
    });
    return Array.from(uniqueMap.values());
  }, [dataOportunity, faseMyOportunityLocal]);

  const paginatedRows = React.useMemo(() => {
    return Array.isArray(filteredRows) ? filteredRows : [];
  }, [filteredRows]);

  // ── Effects ───────────────────────────────────────────────────────────────
  React.useEffect(() => {
    if (totalRegistros === 0) {
      if (currentPage !== 1) setCurrentPage(1);
      return;
    }
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
      return;
    }
    if (currentPage < 1) setCurrentPage(1);
  }, [totalRegistros, totalPages, currentPage]);

  React.useEffect(() => {
    const storedSearch = localStorage.getItem("@myOpp_search");
    const storedFase = localStorage.getItem("@myOpp_fase");
    const storedOriginAccount = localStorage.getItem(
      "@myOpp_oportunityCreator",
    );
    const storedDateStart = localStorage.getItem("@myOpp_dateStart");
    const storedDateEnd = localStorage.getItem("@myOpp_dateEnd");

    if (storedSearch) setSearch(storedSearch);
    if (storedOriginAccount)
      setOportunityCreator(JSON.parse(storedOriginAccount));
    if (storedDateStart) setDateStart(storedDateStart);
    if (storedDateEnd) setDateEnd(storedDateEnd);

    const parsedFase: string[] = storedFase
      ? JSON.parse(storedFase)
      : DEFAULT_PHASES;
    const context = Array.isArray(faseFromContext)
      ? faseFromContext
      : [faseFromContext];

    if (context.length === 0) {
      setFaseMyOportunityLocal((prev) => {
        if (!Array.isArray(prev)) return parsedFase;
        const isSame =
          prev.length === parsedFase.length &&
          prev.every((val, idx) => val === parsedFase[idx]);
        return isSame ? prev : parsedFase;
      });
    }
    setFiltersLoaded(true);
  }, [faseFromContext, faseMyOportunityLocal]);

  React.useEffect(() => {
    if (filtersLoaded && idCorretor) {
      handleGetOportunity(1);
    }
  }, [filtersLoaded, idCorretor]);

  // Ao sair desta tela, limpa o filtro que veio da Home. Sem isso, o
  // `faseHomeFilter` (que vive no contexto, acima desta tela) continuava
  // com o valor antigo mesmo depois de o usuário navegar pra outro lugar e
  // voltar, fazendo o Kanban re-sincronizar com um filtro que já deveria
  // ter sido descartado.
  React.useEffect(() => {
    return () => {
      setFaseHomeFilter([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Flex
      direction="column"
      h={{ base: "full", sm: "full", md: "92vh" }}
      overflow="hidden"
    >
      <Alert
        onOpen={isUnauthorized}
        title="Sessão expirada"
        titleButton={"Fazer login novamente"}
        description={
          "Sua sessão expirou, faça login novamente para ter acesso."
        }
        onClick={() => {
          signOut();
        }}
      />

      {/* ── Título + botões ── */}
      <Flex
        direction={{ base: "column", md: "row" }}
        w="99%"
        h={{ base: undefined, md: "80px" }}
        alignItems="center"
        justifyContent="space-between"
        gap={{ base: 4, md: 0 }}
        mt={{ base: "20px", md: "0px" }}
        px={{ base: 2, md: 0 }}
      >
        <Box
          display="flex"
          flexDir="column"
          alignItems={isMobile ? "flex-start" : "center"}
        >
          <Text fontSize={28} fontWeight="semibold" color={theme.text}>
            Minhas oportunidades
          </Text>
          <Text fontSize={14} color={theme.text50}>
            Selecione uma oportunidade para ver mais detalhes, criar
            agendamentos e mais
          </Text>
        </Box>

        <Flex
          gap={"6px"}
          direction={{ base: "column", sm: "row" }}
          w={{ base: "full", md: 210 }}
        >
          <Button title="Limpar Filtros" onClick={handleClearFilters} />
          <Button
            title={
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap="4px"
              >
                <Icon fontSize="16px" color="white" as={RxUpdate} />
                Atualizar
              </Box>
            }
            onClick={() => {
              if (viewMode === "quadro") {
                setKanbanRefreshKey((k) => k + 1);
              } else {
                handleGetOportunity();
              }
            }}
          />
        </Flex>
      </Flex>

      {/* ── Filtros ── */}
      <Flex
        flexDir="column"
        w="99%"
        h={isMobile ? "auto" : "120px"}
        bg="white"
        borderRadius="8px"
        mt="8px"
        gap="6px"
        p="10px"
        alignItems="center"
        justifyContent="center"
      >
        <Flex w="100%">
          <Text fontWeight="semibold">Pesquisa e filtros</Text>
        </Flex>

        <Flex
          gap="6px"
          flexWrap={isMobile ? "wrap" : "nowrap"}
          flexDirection={isMobile ? "column" : "row"}
          width="100%"
        >
          <Box
            display="flex"
            width={isMobile ? "100%" : "45%"}
            flexDirection={isMobile ? "column" : "row"}
            alignItems={isMobile ? "baseline" : "center"}
          >
            <InputSearch
              isOportunity
              label="Nome do cliente ou ID da oportunidade"
              placeholder={`Pesquisar...`}
              value={search}
              onChange={handleSearchChange}
            />
          </Box>

          <Flex
            width={isMobile ? "100%" : "95%"}
            gap="6px"
            flexDirection={isMobile ? "column" : "row"}
          >
            <InputSelect
              isMulti
              widht="17%"
              widhtMobile="90%"
              title="Fase"
              label={
                (viewMode === "quadro" ? faseKanbanLocal : faseToDisplay)
                  .length !== 0
                  ? handleAdjustNameFase(
                      viewMode === "quadro" ? faseKanbanLocal : faseToDisplay,
                    )
                  : "Selecione uma opção"
              }
              isSelected={
                viewMode === "quadro" ? faseKanbanLocal : faseMyOportunityLocal
              }
              options={optionFase}
              selectedOption={
                viewMode === "quadro" ? faseKanbanLocal : faseMyOportunityLocal
              }
              onChange={handleFaseChange}
            />
            <InputSelect
              isMulti
              widht="17%"
              widhtMobile="90%"
              title="Criador da oportunidade"
              label={
                oportunityCreator.length !== 0
                  ? handleAdjustName(oportunityCreator)
                  : "Selecione uma opção"
              }
              isSelected={oportunityCreator}
              options={optionOpportunityCreator}
              selectedOption={oportunityCreator}
              onChange={handleOportunityCreatorChange}
            />

            <InputSortBy
              ordenarPor={ordenarPor}
              tipoOrdenacao={tipoOrdenacao}
              onChange={handleSortChange}
            />
          </Flex>

          <Flex flexDirection="column" width={isMobile ? "100%" : "35%"}>
            <Text
              fontSize="12px"
              color={theme.text}
              marginLeft="5px"
              height="18px"
            >
              Filtrar por data de criação
            </Text>
            <Flex gap="6px" flexDirection={isMobile ? "column" : "row"}>
              <InputText
                label=""
                name="dateStart"
                placeholder={`De __/__/___`}
                value={dateStart}
                onChange={(e) => handleDateStartChange(e.target.value)}
                maxLength={10}
                iconSee={false}
              />
              <InputText
                label=""
                name="dateEnd"
                placeholder={`Até __/__/___`}
                value={dateEnd}
                onChange={(e) => handleDateEndChange(e.target.value)}
                maxLength={10}
                iconSee={false}
              />
            </Flex>
          </Flex>
        </Flex>
      </Flex>

      {/* ── Tabs + conteúdo ── */}
      <Box
        mt={4}
        bg="white"
        borderRadius="8px"
        w="full"
        flex="1"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        shadow="sm"
      >
        {/* Tabs: Ver como tabela / Ver como quadro */}
        <Flex
          borderBottom="1px solid"
          borderColor="#E5E9F0"
          px="16px"
          pt="4px"
          align="center"
          justify="space-between"
          flexShrink={0}
        >
          <Flex gap={0} w={"350px"}>
            {/* Tab Tabela */}
            <Box
              display="flex"
              alignItems="center"
              gap="6px"
              px="12px"
              py="10px"
              cursor="pointer"
              borderBottom="2px solid"
              borderColor={
                viewMode === "tabela" ? theme.primary : "transparent"
              }
              color={viewMode === "tabela" ? theme.primary : "#94A3B8"}
              fontWeight={viewMode === "tabela" ? "600" : "400"}
              fontSize="13px"
              transition="all 0.15s"
              _hover={{
                color: viewMode === "tabela" ? theme.primary : "#64748B",
              }}
              onClick={() => setViewMode("tabela")}
            >
              <Box w={"30px"}>
                <BsTable width={"18px"} />
              </Box>
              <Text
                fontSize="13px"
                fontWeight="inherit"
                color="inherit"
                w={"fit-content"}
              >
                Ver como tabela
              </Text>
            </Box>

            {/* Tab Quadro */}
            <Box
              display="flex"
              alignItems="center"
              gap="6px"
              px="12px"
              py="10px"
              cursor="pointer"
              borderBottom="2px solid"
              borderColor={
                viewMode === "quadro" ? theme.primary : "transparent"
              }
              color={viewMode === "quadro" ? theme.primary : "#94A3B8"}
              fontWeight={viewMode === "quadro" ? "600" : "400"}
              fontSize="13px"
              transition="all 0.15s"
              _hover={{
                color: viewMode === "quadro" ? theme.primary : "#64748B",
              }}
              onClick={() => {
                if (!hasSyncedKanbanFromHomeRef.current) {
                  const faseInicial = initialFaseHomeFilterRef.current.length > 0
                    ? initialFaseHomeFilterRef.current
                    : Array.isArray(faseMyOportunityLocal) ? faseMyOportunityLocal : [];
                  setFaseKanbanLocal(faseInicial);
                  hasSyncedKanbanFromHomeRef.current = true;
                } else {
                  const currentTableFases = Array.isArray(faseMyOportunityLocal)
                    ? faseMyOportunityLocal
                    : [];
                  setFaseKanbanLocal(currentTableFases);
                }
                setViewMode("quadro");
              }}
            >
              <Box w={"30px"}>
                <BsKanban width={"18px"} />
              </Box>
              <Text
                fontSize="13px"
                fontWeight="inherit"
                color="inherit"
                w={"fit-content"}
              >
                Ver como quadro
              </Text>
            </Box>
          </Flex>

          {/* Botão fullscreen */}
          <IconButton
            aria-label="Ver em tela cheia"
            icon={<FiMaximize2 />}
            size="sm"
            variant="unstyled"
            color="#94A3B8"
            _hover={{ color: theme.primary }}
            onClick={onOpen}
            w="fit-content"
          />
        </Flex>

        {/* Conteúdo */}
        {!filtersLoaded || (viewMode === "tabela" && loading) ? (
          <Flex
            w="100%"
            h={{ base: "400px", xl: "330px", "2xl": "620px" }}
            justify="center"
            align="center"
          >
            <Spinner />
          </Flex>
        ) : (
          <>
            <Box
              w="full"
              flex="1"
              overflow="auto"
              p={viewMode === "quadro" ? "16px" : "0"}
              sx={{
                "&::-webkit-scrollbar": { width: "5px", height: "5px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  background: theme.primary,
                  borderRadius: "3px",
                },
              }}
            >
              {viewMode === "tabela" &&
              !loading &&
              filteredRows.length === 0 ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  width="98%"
                  border="1px solid"
                  borderColor={theme.disable100}
                  padding="5px"
                  margin="5px"
                  sx={{
                    "@media (min-width: 1200px)": {
                      padding: "8px",
                      width: "99%",
                    },
                    "@media (min-width: 768px)": { width: "70%" },
                    "@media (max-width: 425px)": { width: "90%" },
                  }}
                >
                  {!search &&
                  (!Array.isArray(faseMyOportunityLocal) ||
                    faseMyOportunityLocal.length === 0) &&
                  (!Array.isArray(oportunityCreator) ||
                    oportunityCreator.length === 0) &&
                  !dateStart &&
                  !dateEnd ? (
                    <Stack spacing={2}>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontWeight="bold"
                        color={theme.text}
                      >
                        Nenhuma oportunidade encontrada.
                      </Text>
                      <Text fontSize="xs" color={theme.text}>
                        Ajuste os filtros para carregar as oportunidades.
                      </Text>
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      <Text fontSize="xs" fontWeight="bold" color={theme.error}>
                        Nenhuma oportunidade encontrada com os filtros
                        selecionados.
                      </Text>
                      <Text fontSize="xs" color={theme.text}>
                        Isso pode acontecer quando as fases escolhidas não
                        correspondem às oportunidades disponíveis no momento.
                      </Text>
                      <Text fontSize="xs" color={theme.text}>
                        Tente ajustar ou limpar os filtros para visualizar os
                        resultados.
                      </Text>
                    </Stack>
                  )}
                </Box>
              ) : (
                <>
                  {/* Modal tela cheia */}
                  <Modal
                    isOpen={isOpen}
                    onClose={onClose}
                    size="full"
                    scrollBehavior="inside"
                  >
                    <ModalOverlay />
                    <ModalContent
                      maxW="95vw"
                      maxH="95vh"
                      h="95vh"
                      borderRadius="12px"
                    >
                      <ModalHeader>Minhas Oportunidades</ModalHeader>
                      <ModalCloseButton />
                      <ModalBody p={4} display="flex" flexDirection="column">
                        <Box flex="1" overflow="auto">
                          {viewMode === "tabela" ? (
                            <TableMyOportunity
                              data={paginatedRows}
                              onClick={navigation}
                            />
                          ) : (
                            <KanbanMyOportunity
                              idCorretor={idCorretor}
                              search={search}
                              oportunityCreator={oportunityCreator}
                              dateStart={dateStart}
                              dateEnd={dateEnd}
                              selectedFases={faseKanbanLocal.map((f) =>
                                f.toLowerCase(),
                              )}
                              filtro={kanbanFiltroRank.filtro}
                              rank={kanbanFiltroRank.rank}
                              ordenarPor={ordenarPor}
                              tipoOrdenacao={tipoOrdenacao}
                              refreshKey={kanbanRefreshKey}
                              onClickCard={handleClickKanbanCard}
                              renderDropdown={renderDropdown}
                            />
                          )}
                        </Box>

                        {/* Paginação — apenas na visão tabela */}
                        {viewMode === "tabela" && totalPages > 1 && (
                          <Flex
                            justify="flex-start"
                            align="center"
                            py={viewMode === "tabela" ? 3 : 1}
                            px={viewMode === "tabela" ? 4 : 1}
                            gap={2}
                            borderTop="1px solid"
                            borderColor={theme.disable100}
                            bg="white"
                            borderRadius="0 0 8px 8px"
                          >
                            <IconButton
                              aria-label="Página anterior"
                              icon={<MdArrowBack />}
                              size="sm"
                              variant="ghost"
                              isDisabled={currentPage === 1}
                              onClick={() => {
                                if (currentPage > 1)
                                  handleGetOportunity(currentPage - 1);
                              }}
                              color={
                                currentPage === 1
                                  ? theme.disable
                                  : theme.primary
                              }
                              w={"fit-content"}
                            />

                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter((page) => {
                                if (totalPages <= 7) return true;
                                if (page === 1 || page === totalPages)
                                  return true;
                                if (Math.abs(page - currentPage) <= 2)
                                  return true;
                                return false;
                              })
                              .reduce<(number | "...")[]>(
                                (acc, page, idx, arr) => {
                                  if (
                                    idx > 0 &&
                                    (page as number) -
                                      (arr[idx - 1] as number) >
                                      1
                                  )
                                    acc.push("...");
                                  acc.push(page);
                                  return acc;
                                },
                                [],
                              )
                              .map((item, idx) =>
                                item === "..." ? (
                                  <Text
                                    key={`ellipsis-${idx}`}
                                    fontSize="13px"
                                    color={theme.text50}
                                    px={1}
                                    w={"fit-content"}
                                  >
                                    ...
                                  </Text>
                                ) : (
                                  <Box
                                    key={item}
                                    as="button"
                                    px={3}
                                    py={1}
                                    w={"fit-content"}
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor={
                                      currentPage === item
                                        ? theme.primary
                                        : theme.disable100
                                    }
                                    bg={
                                      currentPage === item
                                        ? theme.primary
                                        : "white"
                                    }
                                    color={
                                      currentPage === item
                                        ? "white"
                                        : theme.text
                                    }
                                    cursor="pointer"
                                    fontSize="13px"
                                    fontWeight="medium"
                                    onClick={() =>
                                      handleGetOportunity(item as number)
                                    }
                                    _hover={{ opacity: 0.8 }}
                                  >
                                    {item}
                                  </Box>
                                ),
                              )}

                            <IconButton
                              aria-label="Próxima página"
                              icon={
                                <MdArrowBack
                                  style={{ transform: "rotate(180deg)" }}
                                />
                              }
                              size="sm"
                              variant="ghost"
                              isDisabled={currentPage === totalPages}
                              onClick={() => {
                                if (currentPage < totalPages)
                                  handleGetOportunity(currentPage + 1);
                              }}
                              color={
                                currentPage === totalPages
                                  ? theme.disable
                                  : theme.primary
                              }
                              w={"fit-content"}
                            />
                          </Flex>
                        )}
                      </ModalBody>
                    </ModalContent>
                  </Modal>

                  {!isOpen && (
                    <>
                      {viewMode === "tabela" ? (
                        <TableMyOportunity
                          data={paginatedRows}
                          onClick={navigation}
                        />
                      ) : (
                        <KanbanMyOportunity
                          idCorretor={idCorretor}
                          search={search}
                          oportunityCreator={oportunityCreator}
                          dateStart={dateStart}
                          dateEnd={dateEnd}
                          selectedFases={faseKanbanLocal.map((f) =>
                            f.toLowerCase(),
                          )}
                          filtro={kanbanFiltroRank.filtro}
                          rank={kanbanFiltroRank.rank}
                          ordenarPor={ordenarPor}
                          tipoOrdenacao={tipoOrdenacao}
                          refreshKey={kanbanRefreshKey}
                          onClickCard={handleClickKanbanCard}
                          renderDropdown={renderDropdown}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </Box>

            {/* Paginação — apenas na visão tabela */}
            {viewMode === "tabela" && totalPages > 1 && (
              <Flex
                justify="flex-start"
                align="center"
                py={viewMode === "tabela" ? 3 : 1}
                px={viewMode === "tabela" ? 4 : 1}
                gap={2}
                borderTop="1px solid"
                borderColor={theme.disable100}
                bg="white"
                borderRadius="0 0 8px 8px"
              >
                <IconButton
                  aria-label="Página anterior"
                  icon={<MdArrowBack />}
                  size="sm"
                  variant="ghost"
                  isDisabled={currentPage === 1}
                  onClick={() => {
                    if (currentPage > 1) handleGetOportunity(currentPage - 1);
                  }}
                  color={currentPage === 1 ? theme.disable : theme.primary}
                  w={"fit-content"}
                />

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    if (totalPages <= 7) return true;
                    if (page === 1 || page === totalPages) return true;
                    if (Math.abs(page - currentPage) <= 2) return true;
                    return false;
                  })
                  .reduce<(number | "...")[]>((acc, page, idx, arr) => {
                    if (
                      idx > 0 &&
                      (page as number) - (arr[idx - 1] as number) > 1
                    )
                      acc.push("...");
                    acc.push(page);
                    return acc;
                  }, [])
                  .map((item, idx) =>
                    item === "..." ? (
                      <Text
                        key={`ellipsis-${idx}`}
                        fontSize="13px"
                        color={theme.text50}
                        px={1}
                        w={"fit-content"}
                      >
                        ...
                      </Text>
                    ) : (
                      <Box
                        key={item}
                        as="button"
                        px={3}
                        py={1}
                        w={"fit-content"}
                        borderRadius="md"
                        border="1px solid"
                        borderColor={
                          currentPage === item
                            ? theme.primary
                            : theme.disable100
                        }
                        bg={currentPage === item ? theme.primary : "white"}
                        color={currentPage === item ? "white" : theme.text}
                        cursor="pointer"
                        fontSize="13px"
                        fontWeight="medium"
                        onClick={() => handleGetOportunity(item as number)}
                        _hover={{ opacity: 0.8 }}
                      >
                        {item}
                      </Box>
                    ),
                  )}

                <IconButton
                  aria-label="Próxima página"
                  icon={<MdArrowBack style={{ transform: "rotate(180deg)" }} />}
                  size="sm"
                  variant="ghost"
                  isDisabled={currentPage === totalPages}
                  onClick={() => {
                    if (currentPage < totalPages)
                      handleGetOportunity(currentPage + 1);
                  }}
                  color={
                    currentPage === totalPages ? theme.disable : theme.primary
                  }
                  w={"fit-content"}
                />
              </Flex>
            )}
          </>
        )}
      </Box>
    </Flex>
  );
};

const MyOportunityComponent = () => {
  const { setIndexTabDetails } = React.useContext(OportunityContext);

  const handleNavigation = () => {
    setIndexTabDetails(0);
  };

  return <MyOportunityComponentOriginalWrapper navigation={handleNavigation} />;
};

export default React.memo(MyOportunityComponent);
